import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import styled from "styled-components";
import { AppButton, Icon } from "@/shared/components";
import { creatorOnboardingApi, type CreatorOnboardingProgress } from "@/api/creatorOnboarding";
import { CreatorWelcomeModal } from "./CreatorWelcomeModal";
import { CreatorOnboardingWizard } from "./CreatorOnboardingWizard";
import { OPEN_CREATOR_ONBOARDING_EVENT, openCreatorOnboarding, type OpenCreatorOnboardingEvent } from "../utils/events";
import type { CreatorOnboardingStep } from "@/api/creatorOnboarding";
import { taxDeclarationAPI } from "@/api/taxDeclaration";
import { CREATOR_TAX_DECLARATION_UPDATED_EVENT } from "@/features/creator-tax";
import { MOBILE_GUTTER, MOBILE_NAV_HEIGHT, SAFE_AREA, mediaPhone } from "@/shared/theme/breakpoints";
import { hideWhileTyping } from "@/shared/theme/keyboard";
import {
	TIKTOK_BUSINESS_LINK_ERROR_KEY,
	TIKTOK_BUSINESS_LINK_REOPEN_STEP_KEY,
} from "@/shared/utils/tiktokBusinessOAuth";
import { pressedDarken } from "@/shared/theme/pressed";

interface CreatorOnboardingExperienceProps {
	creatorName: string;
	creatorEmail: string;
}

const AssistantWrap = styled.div`
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 2100;

	/* Phone: above the bottom bar, and below every modal and sheet. */
	${mediaPhone`
		right: calc(${MOBILE_GUTTER}px + ${SAFE_AREA.right});
		bottom: calc(${MOBILE_NAV_HEIGHT}px + 16px + ${SAFE_AREA.bottom});
		z-index: 990;

		/* Pages with a back button hide the bottom bar (see RootLayout), so it moves down. */
		body:has([data-back-button]) & {
			bottom: calc(16px + ${SAFE_AREA.bottom});
		}
	`}

	/* The keyboard would lift the button over the field being typed in. */
	${hideWhileTyping()}
`;

const AssistantButton = styled.button<{ $open: boolean }>`
	position: relative;
	width: 48px;
	height: 48px;
	padding: 10px;
	border: 0;
	border-radius: 50%;
	background: ${({ $open }) => ($open ? "#4e19c4" : "#fff")};
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.24);
	cursor: pointer;
	transition: background 160ms ease;

	&:hover {
		background: #4e19c4;
	}
	.assistant-icon {
		margin: auto;
		width: ${({ $open }) => ($open ? "14px" : "22px")};
		height: ${({ $open }) => ($open ? "14px" : "18px")};
	}
	&:hover .assistant-icon {
		color: #fff;

		path,
		circle,
		rect {
			stroke: #fff;
		}
	}

	${pressedDarken}
`;

const NotificationDot = styled(Icon)`
	position: absolute;
	top: 0;
	right: 0;
`;

const Bubble = styled.div`
	position: absolute;
	right: 0;
	bottom: 64px;
	width: 340px;
	min-height: 319px;

	${mediaPhone`
		max-width: calc(100vw - ${MOBILE_GUTTER * 2}px);
		min-height: 0;
	`}
	padding: 24px;
	border: 1px solid #dde2fa;
	border-radius: 16px;
	background: #fff;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.12);
`;

const BubbleClose = styled.button`
	position: absolute;
	top: 7px;
	right: 7px;
	width: 56px;
	height: 56px;
	padding: 21px;
	border: 0;
	background: transparent;
	cursor: pointer;

	${pressedDarken}
`;

const BubbleTitle = styled.h2`
	margin: 0 0 6px;
	color: #303030;
	font-size: 18px;
	font-weight: 600;
	line-height: 1.25;
`;

const BubbleCopy = styled.p`
	margin: 0 0 24px;
	color: #707070;
	font-size: 12px;
	line-height: 1.45;
`;

const ProgressList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-bottom: 24px;
`;

const ProgressItem = styled.div<{ $complete: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	color: ${({ $complete }) => ($complete ? "#303030" : "#9b9b9b")};
	font-size: 14px;
	font-weight: ${({ $complete }) => ($complete ? 600 : 400)};
	line-height: 24px;

	span {
		display: grid;
		place-items: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: ${({ $complete }) => ($complete ? "#66b026" : "#e6e6e6")};
	}
	span > div {
		width: 12.667px;
		height: 9.333px;
	}
`;

const OpenButton = styled(AppButton)`
	width: 100%;
	height: 40px;
	font-size: 14px;
	font-weight: 600;
`;

const STEP_KEYS = ["socials", "profile", "bank"] as const;

const getRequestedStep = (
	progress: CreatorOnboardingProgress,
	requestedStep?: CreatorOnboardingStep,
): CreatorOnboardingStep => {
	if (!requestedStep) return progress.currentStep;
	const requestedIndex = STEP_KEYS.indexOf(requestedStep);
	const earlierStepIncomplete = STEP_KEYS.slice(0, requestedIndex).some(
		(step) => !progress.completedSteps.includes(step),
	);
	return earlierStepIncomplete ? progress.currentStep : requestedStep;
};

export const CreatorOnboardingExperience = ({ creatorName, creatorEmail }: CreatorOnboardingExperienceProps) => {
	const [progress, setProgress] = useState<CreatorOnboardingProgress | null>(null);
	const [loading, setLoading] = useState(true);
	const [wizardOpen, setWizardOpen] = useState(false);
	const [bubbleOpen, setBubbleOpen] = useState(false);
	const [requestedStep, setRequestedStep] = useState<CreatorOnboardingStep>();
	const [taxDeclared, setTaxDeclared] = useState(false);

	useEffect(() => {
		let active = true;
		creatorOnboardingApi
			.get()
			.then((value) => active && setProgress(value))
			.catch(() => undefined)
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		if (loading) return;
		let active = true;
		const refreshTaxDeclaration = () => {
			void taxDeclarationAPI
				.getMine()
				.then((declaration) => active && setTaxDeclared(Boolean(declaration)))
				.catch(() => active && setTaxDeclared(false));
		};
		refreshTaxDeclaration();
		window.addEventListener(CREATOR_TAX_DECLARATION_UPDATED_EVENT, refreshTaxDeclaration);
		return () => {
			active = false;
			window.removeEventListener(CREATOR_TAX_DECLARATION_UPDATED_EVENT, refreshTaxDeclaration);
		};
	}, [loading]);

	useEffect(() => {
		if (loading) return;
		const linkError = sessionStorage.getItem(TIKTOK_BUSINESS_LINK_ERROR_KEY);
		if (linkError) {
			sessionStorage.removeItem(TIKTOK_BUSINESS_LINK_ERROR_KEY);
			message.error(linkError);
		}
		const reopenStep = sessionStorage.getItem(TIKTOK_BUSINESS_LINK_REOPEN_STEP_KEY);
		if (!reopenStep || !STEP_KEYS.includes(reopenStep as CreatorOnboardingStep)) return;
		sessionStorage.removeItem(TIKTOK_BUSINESS_LINK_REOPEN_STEP_KEY);
		window.dispatchEvent(
			new CustomEvent(OPEN_CREATOR_ONBOARDING_EVENT, {
				detail: { step: reopenStep as CreatorOnboardingStep },
			}),
		);
	}, [loading]);

	// Read via a ref (not the `progress` state directly) so this listener can be
	// attached once on mount and never torn down/re-added. Re-adding on every
	// `progress` change raced with the reopen-after-OAuth dispatch below: when
	// the initial `creatorOnboardingApi.get()` resolves, `setProgress` and
	// `setLoading(false)` land in the same commit (React 18 batches both
	// microtask callbacks), so the listener's cleanup+re-add and the dispatch
	// effect both ran in that same commit — and effect order meant the event
	// fired into the gap between the old listener being removed and the new
	// one being added, silently dropping it.
	const progressRef = useRef(progress);
	useEffect(() => {
		progressRef.current = progress;
	}, [progress]);

	useEffect(() => {
		const handleOpenOnboarding = async (event: Event) => {
			try {
				const currentProgress = progressRef.current;
				const nextProgress = currentProgress ?? (await creatorOnboardingApi.start());
				if (!currentProgress) setProgress(nextProgress);
				setRequestedStep(getRequestedStep(nextProgress, (event as OpenCreatorOnboardingEvent).detail?.step));
				setBubbleOpen(false);
				setWizardOpen(true);
			} catch {
				message.error("Unable to open onboarding. Please try again.");
			}
		};

		const listener = (event: Event) => void handleOpenOnboarding(event);
		window.addEventListener(OPEN_CREATOR_ONBOARDING_EVENT, listener);
		return () => window.removeEventListener(OPEN_CREATOR_ONBOARDING_EVENT, listener);
	}, []);

	useEffect(() => {
		if (loading || progress?.status !== "in_progress") return;
		const url = new URL(window.location.href);
		if (url.searchParams.get("onboarding") !== "open") return;
		url.searchParams.delete("onboarding");
		window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
		openCreatorOnboarding();
	}, [loading, progress?.status]);

	const startFromWelcome = useCallback(async () => {
		try {
			const nextProgress = await creatorOnboardingApi.start();
			setProgress(nextProgress);
			setWizardOpen(true);
		} catch (error) {
			message.error("Unable to start onboarding. Please try again.");
			throw error;
		}
	}, []);

	const handleProgress = (nextProgress: CreatorOnboardingProgress) => {
		setProgress(nextProgress);
		if (nextProgress.status === "completed") {
			setWizardOpen(false);
			setBubbleOpen(false);
		}
	};

	const showAssistant = !loading && progress?.status === "in_progress" && !wizardOpen;

	return (
		<>
			<CreatorWelcomeModal creatorName={creatorName} creatorEmail={creatorEmail} onContinue={startFromWelcome} />
			{progress && (progress.status === "in_progress" || wizardOpen) && (
				<CreatorOnboardingWizard
					open={wizardOpen}
					progress={progress}
					initialStep={requestedStep}
					onProgress={handleProgress}
					onClose={() => {
						setWizardOpen(false);
						setRequestedStep(undefined);
					}}
					onReopen={() => setWizardOpen(true)}
				/>
			)}
			{showAssistant && (
				<AssistantWrap>
					{bubbleOpen && (
						<Bubble role="dialog" aria-label="Creator onboarding progress">
							<BubbleClose
								type="button"
								aria-label="Close onboarding reminder"
								onClick={() => setBubbleOpen(false)}
							>
								<Icon src="/assets/creator-onboarding/close.svg" size={14} color="#454545" />
							</BubbleClose>
							<BubbleTitle>Let&apos;s finish your profile!</BubbleTitle>
							<BubbleCopy>
								We just need a few missing details on your profile before we can send your funds.
								<br />
								It&apos;s super quick!
							</BubbleCopy>
							<ProgressList>
								{[
									{
										key: "socials",
										label: "Link your socials",
										complete: progress.completedSteps.includes("socials"),
									},
									{
										key: "profile",
										label: "Basic profile information",
										complete: progress.completedSteps.includes("profile"),
									},
									{
										key: "bank",
										label: "Bank details",
										complete: progress.completedSteps.includes("bank"),
									},
									{ key: "tax", label: "Tax details", complete: taxDeclared },
								].map(({ key, label, complete }) => {
									return (
										<ProgressItem key={key} $complete={complete}>
											<span>
												<Icon name="onboardingCheck" size={12.667} color="#fff" />
											</span>
											{label}
										</ProgressItem>
									);
								})}
							</ProgressList>
							<OpenButton
								variant="outline"
								type="button"
								onClick={() => {
									setBubbleOpen(false);
									setWizardOpen(true);
								}}
							>
								Open Onboarding
							</OpenButton>
						</Bubble>
					)}
					<AssistantButton
						type="button"
						$open={bubbleOpen}
						aria-label={bubbleOpen ? "Close onboarding reminder" : "Open onboarding reminder"}
						onClick={() => setBubbleOpen((open) => !open)}
					>
						<Icon
							className="assistant-icon"
							src={
								bubbleOpen
									? "/assets/creator-onboarding/close.svg"
									: "/assets/creator-onboarding/assistant.svg"
							}
							size={bubbleOpen ? 18 : 22}
							color={bubbleOpen ? "#fff" : "#4e19c4"}
						/>
						{!bubbleOpen && (
							<NotificationDot
								src="/assets/creator-onboarding/notification-dot.svg"
								size={10}
								preserveOriginalColor
							/>
						)}
					</AssistantButton>
				</AssistantWrap>
			)}
		</>
	);
};
